import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getPageStatus } from '../utils/setupCompletion';
import { WarningTriangle } from './StatusIcons';

// Check completion status for each settings page
function usePageCompletionStatus() {
  const { brand } = useAuth();
  const [status, setStatus] = React.useState({});

  React.useEffect(() => {
    if (!brand) {
      setStatus({});
      return;
    }
    setStatus(getPageStatus(brand));
  }, [brand]);

  return status;
}

// Get indicator color for a specific page
function getPageIndicator(pageStatus) {
  if (!pageStatus) return null;
  if (pageStatus.requiredMissing) return '#DC2626'; // red
  if (pageStatus.recommendedMissing) return '#10c0a5'; // teal
  return null; // complete
}

// Get indicator for Application Setup parent
function getApplicationSetupIndicator(status) {
  const pages = ['branding', 'legal', 'tracking', 'quoter'];
  const hasRequiredMissing = pages.some(p => status[p]?.requiredMissing);
  const hasRecommendedMissing = pages.some(p => status[p]?.recommendedMissing);
  
  if (hasRequiredMissing) return '#DC2626'; // red
  if (hasRecommendedMissing) return '#10c0a5'; // teal
  return null; // complete
}

const getNavItems = (user) => [
  { title: 'Dashboard',            link: '/dashboard',           icon: 'fas fa-chart-pie',            items: [] },
  { title: 'Quote',                 link: null,                   icon: 'fas fa-calculator',
    items: [
      { title: 'Agent Quoter',    link: '/quote' },
      { title: 'Agent Quoter V2', link: '/quote-v2' },
    ],
  },
  { title: 'Leads',                 link: '/reports/leads',       icon: 'fa-regular fa-address-card',  items: [] },
  { title: 'Quote History',         link: '/reports/quotes',      icon: 'fas fa-clock-rotate-left',    items: [] },
  {
    title: 'System Setup',
    link: null,
    icon: 'fas fa-sliders',
    items: [
      { title: 'Customize Branding',    link: '/settings/branding' },
      { title: 'Disclaimers & Legal',   link: '/settings/legal' },
      { title: 'Tracking & Analytics',  link: '/settings/tracking' },
      { title: 'Additional Configuration', link: '/settings/quoter' },
    ],
  },
  { title: 'Companies & Products', link: '/settings/products',   icon: 'fas fa-suitcase',             items: [] },
  { title: 'Engines',              link: '/settings/engines',    icon: 'fas fa-gears',                items: [] },
  {
    title: 'Widgets',
    link: null,
    icon: 'fas fa-toolbox',
    items: [
      { title: 'Version 1',  link: '/settings/widgets' },
      { title: 'Version 2',  link: '/settings/widgets-v2' },
    ],
  },
  { title: 'Link Builder',         link: '/settings/link-builder', icon: 'fas fa-hammer',             items: [] },
  { title: 'Engine URLs',          link: '/settings/engine-urls',  icon: 'fas fa-link',               items: [] },
  { title: 'Lead Delivery',        link: '/settings/integrations', icon: 'fas fa-upload',             items: [] },
  { title: 'Partners',             link: 'https://www.quoteplicity.com/quoteplicity-partners/', icon: 'fa-regular fa-handshake', items: [], external: true },
  ...(user?.admin ? [{
    title: 'Admin',
    link: null,
    icon: 'fas fa-shield-halved',
    items: [
      { title: 'Dashboard',            link: '/admin/dashboard' },
      { title: 'Master Engines',       link: '/admin/engines' },
      { title: 'Companies & Products', link: '/admin/companies' },
      { title: 'Orgs',                 link: '/admin/orgs' },
      { title: 'Users',                link: '/admin/users' },
      { title: 'Sitemap',              link: '/sitemap' },
    ],
  }] : []),
];

const BOTTOM_ITEMS = [
  { title: 'Billing', link: null,    icon: 'fas fa-receipt',      type: 'billing' },
  { title: 'Support', link: 'https://knowledgebase.quoteplicity.com/', icon: 'fas fa-circle-info', external: true },
];

function isPathActive(link, pathname) {
  if (!link) return false;
  if (link === '/dashboard') return pathname === '/dashboard';
  return pathname === link || pathname.startsWith(link + '/');
}

function NavItem({ item, onNavigate, expandedSection, setExpandedSection, location, pageStatus }) {
  const hasChildren = item.items && item.items.length > 0;
  const isExpanded = expandedSection === item.title;

  const active = isPathActive(item.link, location.pathname);
  const childActive = hasChildren && item.items.some(c => isPathActive(c.link, location.pathname));
  const highlighted = active || childActive;

  const handleParentClick = (e) => {
    if (hasChildren) {
      e.preventDefault();
      setExpandedSection(isExpanded ? '' : item.title);
    } else if (item.type === 'billing') {
      e.preventDefault();
    } else {
      onNavigate();
    }
  };

  const baseClasses = 'flex items-center rounded-lg gap-2.5 px-3 py-2.5 w-full text-left transition-colors duration-150 focus:outline-none text-sm';
  const activeClasses = 'bg-blue-50 text-blue-600 font-semibold border border-blue-100';
  const inactiveClasses = 'text-slate-600 hover:text-blue-600 hover:bg-slate-50';

  const itemClasses = `${baseClasses} ${highlighted || (isExpanded && !hasChildren) ? activeClasses : inactiveClasses}`;

  // Get status indicator for Application Setup parent
  let parentIndicator = null;
  if (item.title === 'System Setup') {
    parentIndicator = getApplicationSetupIndicator(pageStatus);
  }

  return (
    <div>
      {item.external ? (
        <a href={item.link} target="_blank" rel="noreferrer" className={itemClasses}>
          <i className={`${item.icon} text-base shrink-0 w-4 text-center`}></i>
          <span>{item.title}</span>
        </a>
      ) : item.link && !hasChildren ? (
        <Link to={item.link} className={itemClasses} onClick={onNavigate}>
          <i className={`${item.icon} text-base shrink-0 w-4 text-center`}></i>
          <span>{item.title}</span>
        </Link>
      ) : (
        <button className={itemClasses} onClick={handleParentClick}>
          <i className={`${item.icon} text-base shrink-0 w-4 text-center`}></i>
          <span className="flex-1 text-left">{item.title}</span>
          {/* Status indicator for Application Setup */}
          {parentIndicator && (
            parentIndicator === '#DC2626' 
              ? <i className="fas fa-exclamation" title="Required setup incomplete" style={{ color: '#DC2626', fontSize: '13px', flexShrink: 0, marginRight: '4px' }}></i>
              : <span style={{ marginRight: '4px' }}><WarningTriangle size={22} title="Recommended setup incomplete" /></span>
          )}
          {hasChildren && (
            <i
              className="fas fa-chevron-right text-xs shrink-0 ml-auto transition-transform duration-200"
              style={{ transform: isExpanded || childActive ? 'rotate(90deg)' : 'rotate(0deg)' }}
            ></i>
          )}
        </button>
      )}

      {/* Sub-items */}
      {hasChildren && (isExpanded || childActive) && (
        <div className="flex flex-col gap-0.5 py-1 pl-7">
          {item.items.map((child) => {
            const cActive = isPathActive(child.link, location.pathname);
            
            // Get status indicator for sub-pages
            let childIndicator = null;
            if (item.title === 'System Setup') {
              if (child.title === 'Customize Branding') childIndicator = getPageIndicator(pageStatus.branding);
              if (child.title === 'Disclaimers & Legal') childIndicator = getPageIndicator(pageStatus.legal);
              if (child.title === 'Tracking & Analytics') childIndicator = getPageIndicator(pageStatus.tracking);
              if (child.title === 'Additional Configuration') childIndicator = getPageIndicator(pageStatus.quoter);
            }
            
            return (
              <Link
                key={child.title + child.link}
                to={child.link}
                className={[
                  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150',
                  cActive ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-slate-500 hover:text-blue-600 hover:bg-slate-50',
                ].join(' ')}
                onClick={onNavigate}
              >
                <i className="fas fa-circle shrink-0" style={{ fontSize: '5px', marginTop: '1px' }}></i>
                <span style={{ flex: 1 }}>{child.title}</span>
                {/* Status indicator icon */}
                {childIndicator && (
                  childIndicator === '#DC2626'
                    ? <i className="fas fa-exclamation" title="Required fields incomplete" style={{ color: '#DC2626', fontSize: '11px', flexShrink: 0 }}></i>
                    : <WarningTriangle size={18} title="Recommended fields incomplete" />
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SidebarNav({ setSidebarOpen }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const pageStatus = usePageCompletionStatus();

  const [expandedSection, setExpandedSection] = useState(() => {
    const p = window.location.pathname;
    if (p.startsWith('/settings')) return 'System Setup';
    if (p.startsWith('/admin')) return 'Admin';
    return '';
  });

  const navItems = getNavItems(user);
  const onNavigate = () => setSidebarOpen && setSidebarOpen(false);

  return (
    <nav
      className="flex flex-col bg-white h-full"
      style={{ borderRight: '1px solid #DDE7F1', width: '256px' }}
      aria-label="sidebar navigation"
      data-testid="sidebar-nav"
    >
      {/* Logo */}
      <div
        className="h-16 flex items-center justify-center shrink-0 px-4"
        style={{ borderBottom: '1px solid #DDE7F1' }}
      >
        <Link to="/dashboard" onClick={onNavigate} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img src="/logo.svg" alt="Quoteplicity" style={{ height: '28px', width: 'auto' }} />
        </Link>
      </div>

      {/* Scrollable nav */}
      <div className="flex flex-col overflow-y-auto flex-1 p-2 gap-0.5">
        {navItems.map((item) => (
          <NavItem
            key={item.title}
            item={item}
            location={location}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            onNavigate={onNavigate}
            pageStatus={pageStatus}
          />
        ))}

        {/* Spacer */}
        <div className="flex-1 min-h-4"></div>

        {/* Bottom section */}
        <div
          className="flex flex-col gap-0.5 pt-2"
          style={{ borderTop: '1px solid #DDE7F1' }}
        >
          {BOTTOM_ITEMS.map((item) => (
            <NavItem
              key={item.title}
              item={item}
              location={location}
              expandedSection={expandedSection}
              setExpandedSection={setExpandedSection}
              onNavigate={onNavigate}
              pageStatus={pageStatus}
            />
          ))}

          {/* Logout */}
          <button
            className="flex items-center rounded-lg gap-2.5 px-3 py-2.5 w-full text-left text-sm text-slate-600 hover:text-red-500 hover:bg-red-50 transition-colors duration-150 mt-0.5"
            onClick={logout}
          >
            <i className="fas fa-arrow-right-from-bracket text-base shrink-0 w-4 text-center"></i>
            <span>Logout</span>
          </button>

          {process.env.NODE_ENV === 'development' && (
            <a
              href="https://subportal.preview.quoteplicity.com"
              data-testid="dev-portal-link"
              className="flex items-center rounded-lg gap-2.5 px-3 py-2.5 w-full text-left text-sm transition-colors duration-150 mt-0.5"
              style={{ color: '#6366F1', textDecoration: 'none' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#EEF2FF'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <i className="fas fa-code text-base shrink-0 w-4 text-center"></i>
              <span>Dev Portal</span>
            </a>
          )}
        </div>
      </div>
    </nav>
  );
}
